<template>
  <div class="view_simulation">
    <!-- FIRST MENU -->
    <transition name="view__sidebartransition">
      <side-bar
        v-if="!sidebarClosed"
        class="sidebar_first"
      >
        <div class="view_simulation__sidebar">
          <template v-if="simulation == null">
            <drawer :title="$t('labels.newSimulation')" :open="true">
              <p v-if="netItems.length === 0" class="message">
                {{ $t("texts.createNetForSimulation") }}
              </p>
              <template v-else>
                <simulation-creation-menu />
              </template>
            </drawer>
            <drawer :title="$t('labels.loadSimulation')" :open="true">
              <simulation-load-menu />
            </drawer>

            <drawer :title="$t('actions.importSimulation')" :open="false">
              <simulation-import-menu />
            </drawer>
          </template>
          <simulation-active-menu v-if="simulation != null" />
        </div>
      </side-bar>
    </transition>

    <!-- SECONDARY MENU -->
    <transition name="menutransition">
      <side-bar
        v-if="!sidebarClosed && settingsOpen && simulation != null"
        class="sidebar_second"
      >
        <simulation-settings-menu @close="settingsOpen = false" />
      </side-bar>
    </transition>

    <div class="main">
      <simulation-net />
      <simulation-progress-bar :content-height="contentHeight" :hide-invisibles="true" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, computed } from "vue";
import { onBeforeRouteLeave } from "vue-router";

import SideBar from "@core/ui/SideBar.vue";
import Drawer from "@core/ui/DrawerBlock.vue";

import SimulationNet from "@editor/views/simulation/SimulationNet.vue";

import SimulationProgressBar from "@editor/items/simulation/SimulationProgressbar.vue";
import SimulationCreationMenu from "@editor/items/simulation/SimulationCreationMenu.vue";
import SimulationLoadMenu from "@editor/items/simulation/SimulationLoadMenu.vue";
import SimulationActiveMenu from "@editor/items/simulation/ActiveSimulation.vue";
import SimulationSettingsMenu from "@editor/items/simulation/SimulationSettings.vue";
import SimulationImportMenu from "@editor/items/simulation/SimulationImportMenu.vue";

import { useSimulationViewStore } from "@editor/views/simulation/simulationView.store";
import { useTargetStore } from "@editor/items/target/targets.store";
import { useResourceStore } from "@editor/items/resource/resources.store";
import { useIndexStore } from "@editor/items/index/indices.store";
import { useNetStore } from "@editor/net/net.store";

const simulationViewStore = useSimulationViewStore();

onBeforeRouteLeave((to, from) => {
  if (simulating.value) {
    console.log("⚠️ still simulating");
    return false;
  }
  if (simulationViewStore.visualizing) {
    // TODO: abort visualization.
    // Abort visualization
    return false;
  }
});

// Get the content height and keep it synchronized to the net element:
const contentHeight = ref<number>();

function updateContentHeight() {
  const netElement = document.querySelector<HTMLElement>(".net__container");
  if (netElement) {
    contentHeight.value = netElement.offsetHeight - 30;
  }
}
onBeforeUnmount(() => {
  window.removeEventListener("resize", updateContentHeight);
});
onMounted(() => {
  updateContentHeight();
});
window.addEventListener("resize", updateContentHeight, {
  passive: true
});

// reload the current simulation.
if (simulationViewStore.openSimulationId) {
  simulationViewStore.loadSimulation({
    simulationId: simulationViewStore.openSimulationId,
    roundIndex: simulationViewStore.selectedRoundIndex
  });
}

const sidebarClosed = computed(() => useNetStore().sidebarClosed);

const simulation = computed(() => simulationViewStore.simulation);
const simulating = computed(() => simulationViewStore.simulating);

const targets = computed(() => {
  return useTargetStore().orderedItems;
});
const resources = computed(() => {
  return useResourceStore().orderedItems;
});
const indices = computed(() => {
  return useIndexStore().orderedItems;
});
const netItems = computed(() => {
  return [...targets.value, ...resources.value, ...indices.value];
});

const settingsOpen = computed({
  get() {
    return useSimulationViewStore().settingsOpen;
  },
  set(value: boolean) {
    useSimulationViewStore().set({ settingsOpen: value });
  }
});
</script>

<style lang="scss">
.view_simulation {
  display: flex;
  // grid-template-columns: var(--sidebar-width, 22rem) minmax(0, 1fr);
  height: 100%;
  width: 100%;

  &__sidebar {
    height: 100%;
    overflow: auto;
  }
}
</style>
